/**
 * AgenticED Admin Layout
 * 
 * Shared layout component for Vendor Portal, District Admin, and Staff dashboards.
 * Includes sidebar navigation, header, and content area.
 * 
 * @version 1.1.0
 */

import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import agenticedLogo from '../../assets/agenticed-logo.png';
import { apiClient } from '../../api/client';
import authService from '../../services/authService.js';
import { isSectionVisible, ADMIN_ZONE_ROLES } from '../../config/pageRoles.js';
import DemoRoleSwitcher from './DemoRoleSwitcher';
import { DemoPill } from './DemoPill';

// ============================================================================
// CONSTANTS
// ============================================================================

const VENDOR_NAV_ITEMS = [
    { path: '/vendor/getting-started', label: 'Getting Started', icon: 'rocket' },
    { path: '/vendor', label: 'Dashboard', icon: 'dashboard' },
    { path: '/vendor/agents', label: 'My Agents', icon: 'agents' },
    { path: '/vendor/sandbox', label: 'Sandbox', icon: 'sandbox' },
    { path: '/vendor/certification', label: 'Certification', icon: 'certification' },
    { path: '/vendor/portfolio', label: 'Partner Portfolio', icon: 'analytics' },
    { path: '/vendor/analytics', label: 'Analytics', icon: 'analytics' },
    { path: '/vendor/docs', label: 'Documentation', icon: 'docs' },
    { path: '/vendor/billing', label: 'Billing', icon: 'billing' },
    { path: '/vendor/team', label: 'Team', icon: 'users' },
    { path: '/vendor/support', label: 'Support', icon: 'support' },
    { path: '/vendor/settings', label: 'Settings', icon: 'settings' },
];

// Foundation Portal — sibling to Vendor / Partner. Two-level navigation per
// architect kickoff: foundation admin lands on Dashboard, then drills into a
// specific initiative. Programs (Flow-2) and Codes/Sponsors (relocated from
// philanthropy in PR-2) appear when the foundation has those flow types.
const FOUNDATION_NAV_ITEMS = [
    { path: '/foundation', label: 'Dashboard', icon: 'dashboard' },
    { path: '/foundation/initiatives', label: 'Initiatives', icon: 'analytics' },
    { path: '/foundation/programs', label: 'Programs', icon: 'agents' },
    { path: '/foundation/recipients', label: 'Recipients', icon: 'users' },
    { path: '/foundation/codes', label: 'Codes', icon: 'docs' },
    { path: '/foundation/team', label: 'Team', icon: 'users' },
    { path: '/foundation/settings', label: 'Settings', icon: 'settings' },
];

// District nav organized by dashboard/workspace view
const DISTRICT_DASHBOARDS = [
    {
        id: 'overview',
        label: 'Overview',
        icon: 'dashboard',
        items: [
            { path: '/district', label: 'District Home', icon: 'dashboard' },
        ]
    },
    {
        id: 'superintendent',
        label: 'Superintendent',
        icon: 'graduation',
        items: [
            { path: '/district/superintendent', label: 'Strategic Dashboard', icon: 'graduation' },
            { path: '/district/value', label: 'Value & Impact', icon: 'value' },
            { path: '/district/initiatives', label: 'Initiatives', icon: 'analytics' },
            { path: '/district/cascade', label: 'Implementation Cascade', icon: 'cascade' },
            { path: '/district/investment-matrix', label: 'Investment Matrix', icon: 'budget' },
            { path: '/district/budget-scenarios', label: 'Budget Scenarios', icon: 'revenue' },
        ]
    },
    {
        id: 'analytics',
        label: 'Analytics',
        icon: 'analytics',
        items: [
            { path: '/district/analytics', label: 'Platform Analytics', icon: 'analytics' },
        ]
    },
    {
        id: 'cio',
        label: 'CIO / Technology',
        icon: 'health',
        items: [
            { path: '/district/cio', label: 'Platform Observability', icon: 'health' },
            { path: '/district/guardian', label: 'Tenant Access Guardian', icon: 'shield' },
            { path: '/district/endpoint-agents', label: 'System Health', icon: 'health' },
            { path: '/district/integrations', label: 'Integration Mgmt', icon: 'integrations' },
            { path: '/district/registry', label: 'EdTech Registry', icon: 'agents' },
            { path: '/district/tech-inventory', label: 'Tech Inventory', icon: 'revenue' },
            { path: '/district/tech-utilization', label: 'Tech Utilization', icon: 'revenue' },
            { path: '/district/tech-roi', label: 'Tech ROI Matrix', icon: 'revenue' },
        ]
    },
    {
        id: 'student-services',
        label: 'Student Services',
        icon: 'benefits',
        items: [
            { path: '/district/counselor',     label: 'Counselor Caseload',      icon: 'users'      },
            { path: '/district/escalations',   label: 'Escalations',             icon: 'escalations'},
            { path: '/district/benefits',      label: 'Benefits Navigator',      icon: 'benefits'   },
            // The reviewer's half of the benefits review gate. It had a <Route> in App.jsx and no
            // link anywhere, so the only way in was to type the URL: a district could be asked to
            // check a determination before the platform acts for a family, with no door to the
            // queue. docs/register-a-declared-route-is-not-a-used-one.md
            { path: '/district/benefits-review', label: 'Benefits Review Queue',  icon: 'escalations'},
            // The implementation half of the benefits engine. Without a door here a
            // district cannot see which programs their families are NOT being told
            // about, or that a draft is waiting on a person to read it — and the
            // whole "gaps close during implementation" promise depends on somebody
            // being able to see the gaps. docs/register-a-declared-route-is-not-a-used-one.md
            { path: '/district/benefit-coverage', label: 'Benefits Coverage',   icon: 'benefits'   },
            { path: '/district/mtss-setup',    label: 'MTSS Setup',              icon: 'settings'   },
            { path: '/district/opportunities', label: 'Scholarships & Opportunities', icon: 'agents'},
            { path: '/district/partners',        label: 'Partner Network',         icon: 'users'      },
            { path: '/district/contracts',       label: 'Partner Contracts',       icon: 'agents'     },
        ]
    },
    {
        id: 'operations',
        label: 'Operations',
        icon: 'workspace',
        items: [
            { path: '/district/workflows', label: 'Workflows', icon: 'workspace' },
            // The human half of the workflow engine. Role-agnostic on purpose: ten
            // distinct assignee_role values hold seeded tasks and counselor is 5 of 13,
            // so a CFO or a safety director must reach their approvals without opening
            // the counselor caseload screen.
            { path: '/district/tasks', label: 'My Approvals', icon: 'escalations' },
            { path: '/district/resources', label: 'Resource Catalog', icon: 'agents' },
            { path: '/marketplace/admin', label: 'Marketplace Admin', icon: 'agents' },
            { path: '/marketplace/partner-vetting', label: 'Marketplace Partner Vetting', icon: 'users' },
            { path: '/workspace/roles', label: 'Role Templates', icon: 'workspace' },
            { path: '/workspace/layouts', label: 'Layout Library', icon: 'workspace' },
            { path: '/workspace/items', label: 'Web Part Catalog', icon: 'workspace' },
            { path: '/workspace/preview', label: 'Preview Workspace', icon: 'preview' },
            { path: '/district/users', label: 'Users & Roles', icon: 'users' },
            { path: '/district/calendar', label: 'Calendar', icon: 'calendar' },
            { path: '/district/knowledge', label: 'Knowledge Base', icon: 'knowledge' },
            { path: '/district/translations', label: 'Content & i18n', icon: 'settings' },
            // Same shape: routed, never linked. This is where a district chooses the languages
            // its families are offered, and an enabled-but-unfilled one renders markers to them.
            { path: '/district/languages', label: 'Languages', icon: 'settings' },
            { path: '/district/context', label: 'Context Config', icon: 'settings' },
            { path: '/district/context-graph-policy', label: 'Context Intelligence', icon: 'settings' },
        ]
    },
    {
        id: 'compliance',
        label: 'Compliance',
        icon: 'consent',
        items: [
            { path: '/district/consent', label: 'Consent & Conveyance', icon: 'consent' },
            { path: '/district/audit', label: 'Audit Log', icon: 'audit' },
            { path: '/district/compliance', label: 'Compliance Reports', icon: 'consent' },
            { path: '/district/billing', label: 'Billing', icon: 'billing' },
            { path: '/district/settings', label: 'Settings', icon: 'settings' },
        ]
    },
];

// Flat list for backwards compat (used by other portals)
const DISTRICT_NAV_ITEMS = DISTRICT_DASHBOARDS.flatMap(d => d.items);

const STAFF_NAV_ITEMS = [
    { path: '/staff', label: 'Dashboard', icon: 'dashboard' },
    { path: '/staff/tenants', label: 'Tenants', icon: 'tenants' },
    { path: '/staff/platform-value', label: 'Platform Value', icon: 'value' },
    { path: '/staff/revenue', label: 'Revenue', icon: 'revenue' },
    { path: '/staff/pricing', label: 'Pricing Config', icon: 'revenue' },
    { path: '/staff/vouchers', label: 'Vouchers', icon: 'vouchers' },
    { path: '/staff/support', label: 'Support Queue', icon: 'support' },
    { path: '/staff/vendors', label: 'Vendors', icon: 'vendors' },
    { path: '/staff/agents', label: 'Agent Registry', icon: 'agents' },
    { path: '/staff/users', label: 'Staff Users', icon: 'users' },
    { path: '/staff/health', label: 'System Health', icon: 'health' },
    { path: '/staff/flags', label: 'Feature Flags', icon: 'flags' },
    { path: '/staff/cobrowse', label: 'Co-browse Templates', icon: 'cobrowse' },
    { path: '/staff/registry-data', label: 'Registry Data', icon: 'data' },
    { path: '/staff/registry', label: 'EdTech Registry', icon: 'agents' },
    { path: '/staff/partner-curation', label: 'Partner Curation', icon: 'users' },
    { path: '/staff/audit', label: 'Audit Log', icon: 'audit' }
];

// ============================================================================
// ICON COMPONENT
// ============================================================================

function NavIcon({ name, className = '' }) {
    const icons = {
        dashboard: (
            <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <rect x="3" y="3" width="7" height="7" rx="1" />
                <rect x="14" y="3" width="7" height="7" rx="1" />
                <rect x="3" y="14" width="7" height="7" rx="1" />
                <rect x="14" y="14" width="7" height="7" rx="1" />
            </svg>
        ),
        agents: (
            <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="12" cy="8" r="4" />
                <path d="M4 20c0-4 4-6 8-6s8 2 8 6" />
                <path d="M16 4l2 2-2 2" />
            </svg>
        ),
        certification: (
            <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M12 2l3 6 6 1-4 4 1 6-6-3-6 3 1-6-4-4 6-1z" />
            </svg>
        ),
        analytics: (
            <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M3 3v18h18" />
                <path d="M7 16l4-4 4 4 5-6" />
            </svg>
        ),
        users: (
            <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="9" cy="7" r="4" />
                <path d="M1 21v-2a4 4 0 014-4h8a4 4 0 014 4v2" />
                <path d="M16 3.13a4 4 0 010 7.75" />
                <path d="M21 21v-2a4 4 0 00-3-3.85" />
            </svg>
        ),
        budget: (
            <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="12" cy="12" r="10" />
                <path d="M12 6v12M8 10h8M8 14h8" />
            </svg>
        ),
        consent: (
            <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
                <path d="M9 12l2 2 4-4" />
            </svg>
        ),
        calendar: (
            <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
                <line x1="16" y1="2" x2="16" y2="6" />
                <line x1="8" y1="2" x2="8" y2="6" />
                <line x1="3" y1="10" x2="21" y2="10" />
            </svg>
        ),
        audit: (
            <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z" />
                <path d="M14 2v6h6" />
                <path d="M16 13H8M16 17H8M10 9H8" />
            </svg>
        ),
        settings: (
            <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="12" cy="12" r="3" />
                <path d="M19.4 15a1.65 1.65 0 00.33 1.82l.06.06a2 2 0 010 2.83 2 2 0 01-2.83 0l-.06-.06a1.65 1.65 0 00-1.82-.33 1.65 1.65 0 00-1 1.51V21a2 2 0 01-2 2 2 2 0 01-2-2v-.09A1.65 1.65 0 009 19.4a1.65 1.65 0 00-1.82.33l-.06.06a2 2 0 01-2.83 0 2 2 0 010-2.83l.06-.06a1.65 1.65 0 00.33-1.82 1.65 1.65 0 00-1.51-1H3a2 2 0 01-2-2 2 2 0 012-2h.09A1.65 1.65 0 004.6 9a1.65 1.65 0 00-.33-1.82l-.06-.06a2 2 0 010-2.83 2 2 0 012.83 0l.06.06a1.65 1.65 0 001.82.33H9a1.65 1.65 0 001-1.51V3a2 2 0 012-2 2 2 0 012 2v.09a1.65 1.65 0 001 1.51 1.65 1.65 0 001.82-.33l.06-.06a2 2 0 012.83 0 2 2 0 010 2.83l-.06.06a1.65 1.65 0 00-.33 1.82V9a1.65 1.65 0 001.51 1H21a2 2 0 012 2 2 2 0 01-2 2h-.09a1.65 1.65 0 00-1.51 1z" />
            </svg>
        ),
        logout: (
            <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M9 21H5a2 2 0 01-2-2V5a2 2 0 012-2h4" />
                <polyline points="16 17 21 12 16 7" />
                <line x1="21" y1="12" x2="9" y2="12" />
            </svg>
        ),
        menu: (
            <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <line x1="3" y1="12" x2="21" y2="12" />
                <line x1="3" y1="6" x2="21" y2="6" />
                <line x1="3" y1="18" x2="21" y2="18" />
            </svg>
        ),
        close: (
            <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <line x1="18" y1="6" x2="6" y2="18" />
                <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
        ),
        tenants: (
            <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M3 9l9-7 9 7v11a2 2 0 01-2 2H5a2 2 0 01-2-2z" />
                <polyline points="9 22 9 12 15 12 15 22" />
            </svg>
        ),
        vendors: (
            <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <rect x="2" y="7" width="20" height="14" rx="2" ry="2" />
                <path d="M16 21V5a2 2 0 00-2-2h-4a2 2 0 00-2 2v16" />
            </svg>
        ),
        value: (
            <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <polyline points="23 6 13.5 15.5 8.5 10.5 1 18" />
                <polyline points="17 6 23 6 23 12" />
            </svg>
        ),
        billing: (
            <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <rect x="1" y="4" width="22" height="16" rx="2" ry="2" />
                <line x1="1" y1="10" x2="23" y2="10" />
            </svg>
        ),
        escalations: (
            <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" />
                <line x1="12" y1="9" x2="12" y2="13" />
                <line x1="12" y1="17" x2="12.01" y2="17" />
            </svg>
        ),
        revenue: (
            <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <line x1="12" y1="1" x2="12" y2="23" />
                <path d="M17 5H9.5a3.5 3.5 0 000 7h5a3.5 3.5 0 010 7H6" />
            </svg>
        ),
        vouchers: (
            <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <rect x="1" y="4" width="22" height="16" rx="2" ry="2" />
                <line x1="1" y1="10" x2="23" y2="10" />
                <path d="M7 15h.01M11 15h2" />
            </svg>
        ),
        support: (
            <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M21 11.5a8.38 8.38 0 01-.9 3.8 8.5 8.5 0 01-7.6 4.7 8.38 8.38 0 01-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 01-.9-3.8 8.5 8.5 0 014.7-7.6 8.38 8.38 0 013.8-.9h.5a8.48 8.48 0 018 8v.5z" />
            </svg>
        ),
        health: (
            <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M22 12h-4l-3 9L9 3l-3 9H2" />
            </svg>
        ),
        flags: (
            <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M4 15s1-1 4-1 5 2 8 2 4-1 4-1V3s-1 1-4 1-5-2-8-2-4 1-4 1z" />
                <line x1="4" y1="22" x2="4" y2="15" />
            </svg>
        ),
        cobrowse: (
            <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M15 15l-2 5L9 9l11 4-5 2z" />
                <path d="M15 15l5 5" />
                <circle cx="12" cy="12" r="10" />
            </svg>
        ),
        workspace: (
            <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
                <line x1="3" y1="9" x2="21" y2="9" />
                <line x1="9" y1="21" x2="9" y2="9" />
            </svg>
        ),
        rocket: (
            <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M4.5 16.5c-1.5 1.26-2 5-2 5s3.74-.5 5-2c.71-.84.7-2.13-.09-2.91a2.18 2.18 0 00-2.91-.09z" />
                <path d="M12 15l-3-3a22 22 0 012-3.95A12.88 12.88 0 0122 2c0 2.72-.78 7.5-6 11a22.35 22.35 0 01-4 2z" />
                <path d="M9 12H4s.55-3.03 2-4c1.62-1.08 5 0 5 0" />
                <path d="M12 15v5s3.03-.55 4-2c1.08-1.62 0-5 0-5" />
            </svg>
        ),
        sandbox: (
            <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <rect x="2" y="3" width="20" height="14" rx="2" ry="2" />
                <line x1="8" y1="21" x2="16" y2="21" />
                <line x1="12" y1="17" x2="12" y2="21" />
            </svg>
        ),
        docs: (
            <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z" />
                <polyline points="14 2 14 8 20 8" />
                <line x1="16" y1="13" x2="8" y2="13" />
                <line x1="16" y1="17" x2="8" y2="17" />
            </svg>
        ),
        knowledge: (
            <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M2 3h6a4 4 0 014 4v14a3 3 0 00-3-3H2z" />
                <path d="M22 3h-6a4 4 0 00-4 4v14a3 3 0 013-3h7z" />
            </svg>
        ),
        benefits: (
            <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M20.84 4.61a5.5 5.5 0 00-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 00-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 000-7.78z" />
            </svg>
        ),
        integrations: (
            <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="12" cy="12" r="3" />
                <path d="M12 1v4M12 19v4M4.22 4.22l2.83 2.83M16.95 16.95l2.83 2.83M1 12h4M19 12h4M4.22 19.78l2.83-2.83M16.95 7.05l2.83-2.83" />
            </svg>
        ),
        graduation: (
            <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M22 10l-10-6L2 10l10 6 10-6z" />
                <path d="M6 12v5c0 2 3 3 6 3s6-1 6-3v-5" />
                <line x1="22" y1="10" x2="22" y2="16" />
            </svg>
        ),
        chevronDown: (
            <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <polyline points="6 9 12 15 18 9" />
            </svg>
        ),
        chevronRight: (
            <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <polyline points="9 18 15 12 9 6" />
            </svg>
        ),
        // Zone icons for 3-Zone Model
        briefcase: (
            <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <rect x="2" y="7" width="20" height="14" rx="2" ry="2" />
                <path d="M16 21V5a2 2 0 00-2-2h-4a2 2 0 00-2 2v16" />
            </svg>
        ),
        book: (
            <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M2 3h6a4 4 0 014 4v14a3 3 0 00-3-3H2z" />
                <path d="M22 3h-6a4 4 0 00-4 4v14a3 3 0 013-3h7z" />
            </svg>
        ),
        shield: (
            <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
            </svg>
        ),
        cascade: (
            <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M12 3v4M12 7l-6 4M12 7l6 4" />
                <circle cx="6" cy="12" r="2" />
                <circle cx="18" cy="12" r="2" />
                <path d="M6 14v3M18 14v3M6 17l6 4M18 17l-6 4" />
                <circle cx="12" cy="3" r="1.5" fill="currentColor" />
            </svg>
        ),
        preview: (
            <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                <circle cx="12" cy="12" r="3" />
            </svg>
        )
    };

    return icons[name] || null;
}

// ============================================================================
// 3-ZONE NAVIGATION (DISTRICT)
// ============================================================================

function ZoneHeader({ icon, label, color }) {
    return (
        <div className={`flex items-center gap-2 px-3 py-2 mt-3 mb-1`}>
            <NavIcon name={icon} className={`w-4 h-4 ${color}`} />
            <span className={`text-xs font-bold uppercase tracking-wider ${color}`}>{label}</span>
        </div>
    );
}

function AdminSection({ section, expandedSections, toggleSection, location, navigate, onClose, partnerPending }) {
    const isExpanded = expandedSections.includes(section.id);
    const hasActiveItem = section.items.some(item =>
        location.pathname === item.path ||
        (item.path !== '/district' && location.pathname.startsWith(item.path))
    );

    return (
        <div className="mb-1">
            <button
                onClick={() => {
                    toggleSection(section.id);
                    if (section.items.length === 1) {
                        navigate(section.items[0].path);
                        onClose();
                    }
                }}
                className={`
                    w-full flex items-center justify-between px-3 py-2 rounded-lg
                    transition-colors duration-150
                    ${hasActiveItem
                        ? 'text-white'
                        : 'text-slate-400 hover:bg-slate-800 hover:text-white'
                    }
                `}
            >
                <div className="flex items-center gap-3">
                    <NavIcon name={section.icon} className="w-5 h-5 flex-shrink-0" />
                    <span className="text-sm font-semibold">{section.label}</span>
                </div>
                {section.items.length > 1 && (
                    <NavIcon
                        name={isExpanded ? 'chevronDown' : 'chevronRight'}
                        className="w-4 h-4 flex-shrink-0 text-slate-500"
                    />
                )}
            </button>

            {isExpanded && section.items.length > 1 && (
                <div className="ml-4 mt-1 space-y-0.5 border-l border-slate-700 pl-3">
                    {section.items.map((item) => {
                        const isActive = location.pathname === item.path ||
                            (item.path !== '/district' && location.pathname.startsWith(item.path));

                        return (
                            <Link
                                key={item.path}
                                to={item.path}
                                onClick={onClose}
                                className={`
                                    flex items-center gap-3 px-3 py-2 rounded-lg
                                    transition-colors duration-150 text-sm
                                    ${isActive
                                            ? 'bg-slate-700 text-white'
                                            : 'text-slate-400 hover:bg-slate-800 hover:text-white'
                                    }
                                `}
                            >
                                <NavIcon name={item.icon} className="w-4 h-4 flex-shrink-0" />
                                <span className="font-medium">{item.label}</span>
                                {item.path === '/district/partners' && partnerPending > 0 && (
                                    <span className="ml-auto inline-flex items-center justify-center min-w-[1.25rem]
                                                     h-5 px-1.5 rounded-full bg-violet-600 text-white text-[10px] font-semibold">
                                        {partnerPending}
                                    </span>
                                )}
                            </Link>
                        );
                    })}
                </div>
            )}
        </div>
    );
}

function DistrictThreeZoneNav({ role, expandedSections, toggleSection, location, navigate, onClose, partnerPending }) {
    const showAdmin = ADMIN_ZONE_ROLES.includes(role);
    // Phase 1.6: My Workspace points at /workspace (Canvas SPA).
    // /district/workspace now Navigate-redirects to /workspace, so match
    // either path for the active-state highlight.
    const workspaceActive = location.pathname === '/district/workspace' || location.pathname === '/workspace';
    const growthActive = location.pathname === '/district/growth';

    // Filter admin sections based on role
    const visibleSections = DISTRICT_DASHBOARDS.filter(section =>
        isSectionVisible(role, section.id)
    );

    return (
        <>
            {/* Zone 1: My Workspace */}
            <ZoneHeader icon="briefcase" label="My Workspace" color="text-blue-400" />
            <a
                href="/workspace"
                target="_blank"
                rel="noopener noreferrer"
                onClick={onClose}
                className={`
                    flex items-center gap-3 px-3 py-2.5 rounded-lg
                    transition-colors duration-150
                    ${workspaceActive
                        ? 'bg-slate-700 text-white'
                        : 'text-slate-400 hover:bg-slate-800 hover:text-white'
                    }
                `}
            >
                <NavIcon name="dashboard" className="w-5 h-5 flex-shrink-0" />
                <span className="text-sm font-medium">My Dashboard</span>
                <svg className="w-3 h-3 ml-auto opacity-40 shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M18 13v6a2 2 0 01-2 2H5a2 2 0 01-2-2V8a2 2 0 012-2h6"/><polyline points="15 3 21 3 21 9"/><line x1="10" y1="14" x2="21" y2="3"/></svg>
            </a>

            {/* Zone 2: My Growth */}
            <ZoneHeader icon="book" label="My Growth" color="text-emerald-400" />
            <Link
                to="/district/growth"
                onClick={onClose}
                className={`
                    flex items-center gap-3 px-3 py-2.5 rounded-lg
                    transition-colors duration-150
                    ${growthActive
                        ? 'bg-slate-700 text-white'
                        : 'text-slate-400 hover:bg-slate-800 hover:text-white'
                    }
                `}
            >
                <NavIcon name="book" className="w-5 h-5 flex-shrink-0" />
                <span className="text-sm font-medium">Professional Development</span>
            </Link>

            {/* Zone 3: Administration (role-gated) */}
            {showAdmin && visibleSections.length > 0 && (
                <>
                    <ZoneHeader icon="shield" label="Administration" color="text-purple-400" />
                    {visibleSections.map((section) => (
                        <AdminSection
                            key={section.id}
                            section={section}
                            expandedSections={expandedSections}
                            toggleSection={toggleSection}
                            location={location}
                            navigate={navigate}
                            onClose={onClose}
                            partnerPending={partnerPending}
                        />
                    ))}
                </>
            )}
        </>
    );
}

// ============================================================================
// SIDEBAR COMPONENT
// ============================================================================

function Sidebar({ type, isOpen, onClose, profile, isSuperuser }) {
    const location = useLocation();
    const navigate = useNavigate();
    const [showPortalSwitcher, setShowPortalSwitcher] = useState(false);
    const currentRole = authService.getRole() || 'district';

    // Load tenant branding: try localStorage first, then fetch from API
    const [branding, setBranding] = useState(null);
    useEffect(() => {
        try {
            const stored = localStorage.getItem('agenticed_tenant_branding');
            if (stored) setBranding(JSON.parse(stored));
        } catch { /* ignore */ }

        // Fetch latest branding from API (updates stale localStorage)
        if (type === 'district') {
            apiClient.getDistrictBranding()
                .then(data => {
                    if (data?.branding) {
                        setBranding(data.branding);
                        localStorage.setItem('agenticed_tenant_branding', JSON.stringify(data.branding));
                    }
                })
                .catch(() => { /* use cached branding */ });
        }

        // Listen for branding updates from Settings page (live refresh)
        const handleBrandingUpdate = (e) => setBranding(e.detail);
        window.addEventListener('branding-updated', handleBrandingUpdate);
        return () => window.removeEventListener('branding-updated', handleBrandingUpdate);
    }, [type]);

    // Partner Network pending-confirmation count — drives the sidebar badge.
    // Global-to-the-page signal: shown regardless of which Partner Network
    // view the coordinator is in.
    const [partnerPending, setPartnerPending] = useState(0);
    useEffect(() => {
        if (type !== 'district') return;
        apiClient.request('/api/partners/invitations?status=pending_confirmation&coordinator_id=me')
            .then(data => setPartnerPending(Array.isArray(data?.invitations) ? data.invitations.length : 0))
            .catch(() => { /* badge stays hidden on failure */ });
    }, [type]);

    const [expandedSections, setExpandedSections] = useState(() => {
        // Auto-expand the section containing the current path
        if (type === 'district') {
            const current = DISTRICT_DASHBOARDS.find(d =>
                d.items.some(item => location.pathname === item.path ||
                    (item.path !== '/district' && location.pathname.startsWith(item.path)))
            );
            return current ? [current.id] : ['overview'];
        }
        return [];
    });
    const navItems = type === 'foundation' ? FOUNDATION_NAV_ITEMS
                  : type === 'vendor'      ? VENDOR_NAV_ITEMS
                  : type === 'staff'       ? STAFF_NAV_ITEMS
                  :                          DISTRICT_NAV_ITEMS;
    const useGroupedNav = type === 'district';

    const toggleSection = (sectionId) => {
        setExpandedSections(prev =>
            prev.includes(sectionId)
                ? prev.filter(id => id !== sectionId)
                : [...prev, sectionId]
        );
    };

    // Auto-expand section when navigating to a page within it
    useEffect(() => {
        if (type === 'district') {
            const current = DISTRICT_DASHBOARDS.find(d =>
                d.items.some(item => location.pathname === item.path ||
                    (item.path !== '/district' && location.pathname.startsWith(item.path)))
            );
            if (current) {
                setExpandedSections(prev =>
                    prev.includes(current.id) ? prev : [...prev, current.id]
                );
            }
        }
    }, [location.pathname, type]);

    const handleLogout = () => {
        localStorage.removeItem('agenticed_admin_auth');
        // Clear the foundation session too, so signing out of the Foundation
        // Portal re-arms a fresh picker entry (no stale agenticed_foundation_auth
        // token silently re-authing on the next visit). No-op for non-foundation.
        localStorage.removeItem('agenticed_foundation_auth');
        // And the bearer token every data store actually sends. This line was
        // missing, and its absence was the reason signing out and back in did
        // not fix a 401: `agenticed_session` is read FIRST by ~16 stores and
        // pages, so the token this logout left behind kept shadowing the fresh
        // one the next login minted. Exit-to-Login below already cleared it,
        // which is why one exit worked and the other did not — the same button,
        // to a user, with opposite results.
        localStorage.removeItem('agenticed_session');
        navigate('/login');
    };

    // Exit the FSF demo back to the persona picker (the demo "login"). Clears
    // the demo session tokens and hard-navigates to /admin/fsf so it works
    // regardless of which portal/app you're in.
    const handleExitDemo = () => {
        localStorage.removeItem('agenticed_admin_auth');
        localStorage.removeItem('agenticed_foundation_auth');
        localStorage.removeItem('agenticed_session');
        window.location.assign('/admin/fsf');
    };

    const handlePortalSwitch = async (newType) => {
        setShowPortalSwitcher(false);

        // Leaving the Foundation Portal — drop the minted foundation_session
        // token so re-entry goes back through the superuser picker fresh
        // (the explicit-pick flow, never a stale-token auto-enter).
        if (type === 'foundation') {
            localStorage.removeItem('agenticed_foundation_auth');
        }

        if (newType === 'cbo') {
            // Superuser CBO entry uses a dedicated picker page that lists
            // available orgs, surfaces backend errors, and handles the token
            // exchange. Avoids window.prompt and silent redirects-to-login.
            navigate('/cbo/switch');
            return;
        }

        if (newType === 'foundation') {
            // Superuser Foundation entry uses a dedicated picker page that
            // lists active philanthropy sponsors and mints a foundation_session
            // JWT via /api/foundation/auth/superuser-enter. The sponsor identity
            // MUST come from an explicit pick — no demo/default sponsor.
            navigate('/foundation/switch');
            return;
        }

        // Standard portal switch (district / vendor / staff)
        const auth = localStorage.getItem('agenticed_admin_auth');
        if (auth) {
            try {
                const session = JSON.parse(auth);
                session.type = newType;
                if (newType === 'district') {
                    session.tenantId = session.tenantId || 'demo-district';
                } else if (newType === 'vendor') {
                    session.vendorId = session.vendorId || 'demo-vendor';
                }
                localStorage.setItem('agenticed_admin_auth', JSON.stringify(session));
                window.location.href = `/admin/${newType}`;
            } catch (e) {
                // Ignore parse errors
            }
        }
    };

    const portals = [
        { type: 'staff', label: 'Staff Portal', color: 'blue', icon: 'users' },
        { type: 'district', label: 'District Admin', color: 'purple', icon: 'tenants' },
        { type: 'vendor', label: 'Vendor Portal', color: 'teal', icon: 'vendors' },
        { type: 'cbo', label: 'Partner Portal', color: 'orange', icon: 'agents' },
        { type: 'foundation', label: 'Foundation Portal', color: 'amber', icon: 'analytics' }
    ];

    return (
        <>
            {/* Mobile overlay */}
            {isOpen && (
                <div 
                    className="fixed inset-0 bg-black/50 z-40 lg:hidden"
                    onClick={onClose}
                />
            )}

            {/* Sidebar */}
            <aside className={`
                fixed top-0 left-0 z-50 h-full w-64 ${(type === "vendor" || type === "foundation") ? "bg-white border-r border-slate-200" : "bg-slate-900"}
                transform transition-transform duration-200 ease-in-out
                lg:translate-x-0 lg:static lg:z-auto
                ${isOpen ? 'translate-x-0' : '-translate-x-full'}
            `}>
                {/* Logo - respects tenant branding settings */}
                <div className={`h-16 flex items-center justify-between px-4 border-b ${(type === "vendor" || type === "foundation") ? "border-slate-200" : "border-slate-700"}`}>
                    <Link to={type === 'vendor' ? '/vendor' : type === 'foundation' ? '/foundation' : type === 'staff' ? '/staff' : '/district/workspace'} className="flex items-center">
                        {branding?.mode === 'district_branded' && branding?.districtLogoUrl ? (
                            <img
                                src={branding.districtLogoUrl}
                                alt="District"
                                className="h-8 w-auto"
                                onError={(e) => { e.target.src = agenticedLogo; }}
                            />
                        ) : (
                            <img
                                src={agenticedLogo}
                                alt="AgenticED"
                                className="h-8 w-auto"
                            />
                        )}
                    </Link>
                    <button 
                        onClick={onClose}
                        className="lg:hidden p-1 text-slate-400 hover:text-white"
                    >
                        <NavIcon name="close" className="w-5 h-5" />
                    </button>
                </div>

                {/* Portal badge with switcher */}
                <div className={`px-4 py-3 border-b ${(type === "vendor" || type === "foundation") ? "border-slate-200" : "border-slate-700"}`}>
                    <div className="flex items-center justify-between">
                        <span className={`
                            inline-flex items-center px-2.5 py-0.5 rounded text-xs font-medium
                            ${type === 'vendor'
                                ? 'bg-teal-500/20 text-teal-300'
                                : type === 'foundation'
                                ? 'bg-amber-500/20 text-amber-300'
                                : type === 'staff'
                                ? 'bg-blue-500/20 text-blue-300'
                                : type === 'cbo'
                                ? 'bg-orange-500/20 text-orange-300'
                                : 'bg-purple-500/20 text-purple-300'
                            }
                        `}>
                            {type === 'vendor' ? 'Vendor Portal' : type === 'foundation' ? 'Foundation Portal' : type === 'staff' ? 'AgenticED Staff' : type === 'cbo' ? 'Partner Portal' : 'District Admin'}
                        </span>
                        {isSuperuser && (
                            <button
                                onClick={() => setShowPortalSwitcher(!showPortalSwitcher)}
                                className="p-1 text-slate-400 hover:text-white rounded transition-colors"
                                title="Switch Portal"
                            >
                                <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                    <polyline points="7 10 12 5 17 10" />
                                    <polyline points="7 14 12 19 17 14" />
                                </svg>
                            </button>
                        )}
                    </div>

                    {/* Portal Switcher Dropdown */}
                    {showPortalSwitcher && isSuperuser && (
                        <div className="mt-2 py-1 bg-slate-800 rounded-lg border border-slate-600">
                            {portals.filter(p => p.type !== type).map((portal) => (
                                <button
                                    key={portal.type}
                                    onClick={() => handlePortalSwitch(portal.type)}
                                    className="w-full flex items-center gap-2 px-3 py-2 text-sm text-slate-300 hover:bg-slate-700 transition-colors"
                                >
                                    <NavIcon name={portal.icon} className="w-4 h-4" />
                                    <span>Switch to {portal.label}</span>
                                </button>
                            ))}
                        </div>
                    )}
                </div>

                {/* Navigation */}
                <nav className="flex-1 px-2 py-4 space-y-1 overflow-y-auto">
                    {useGroupedNav ? (
                        /* 3-Zone navigation for district portal */
                        <DistrictThreeZoneNav
                            role={currentRole}
                            expandedSections={expandedSections}
                            toggleSection={toggleSection}
                            location={location}
                            navigate={navigate}
                            onClose={onClose}
                            partnerPending={partnerPending}
                        />
                    ) : (
                        /* Flat navigation for vendor and staff portals */
                        navItems.map((item) => {
                            const isActive = location.pathname === item.path ||
                                (item.path !== `/${type}` && location.pathname.startsWith(item.path));

                            return (
                                <Link
                                    key={item.path}
                                    to={item.path}
                                    onClick={onClose}
                                    className={`
                                        flex items-center gap-3 px-3 py-2.5 rounded-lg
                                        transition-colors duration-150
                                        ${isActive
                                            ? (type === 'vendor' ? 'bg-slate-100 text-slate-900' : 'bg-slate-700 text-white')
                                            : (type === 'vendor' ? 'text-slate-600 hover:bg-slate-100 hover:text-slate-900' : 'text-slate-400 hover:bg-slate-800 hover:text-white')
                                        }
                                    `}
                                >
                                    <NavIcon name={item.icon} className="w-5 h-5 flex-shrink-0" />
                                    <span className="text-sm font-medium">{item.label}</span>
                                </Link>
                            );
                        })
                    )}
                </nav>

                {/* Demo Role Switcher (only renders for demo sessions) */}
                <DemoRoleSwitcher />

                {/* User profile */}
                <div className={`p-4 border-t ${(type === "vendor" || type === "foundation") ? "border-slate-200" : "border-slate-700"}`}>
                    {profile && (
                        <div className="flex items-center gap-3 mb-3">
                            <div className="w-9 h-9 rounded-full bg-gradient-to-br from-blue-500 to-purple-500 flex items-center justify-center">
                                <span className="text-white text-sm font-medium">
                                    {profile.name?.charAt(0) || 'U'}
                                </span>
                            </div>
                            <div className="flex-1 min-w-0">
                                <p className={`text-sm font-medium truncate ${type === "vendor" ? "text-slate-900" : "text-white"}`}>
                                    {profile.name || 'User'}
                                </p>
                                <p className="text-xs text-slate-400 truncate">
                                    {profile.email || profile.company_name || ''}
                                </p>
                            </div>
                        </div>
                    )}
                    <button
                        onClick={handleLogout}
                        className="w-full flex items-center gap-3 px-3 py-2 rounded-lg
                            text-slate-400 hover:bg-slate-800 hover:text-white
                            transition-colors duration-150"
                    >
                        <NavIcon name="logout" className="w-5 h-5" />
                        <span className="text-sm font-medium">Sign Out</span>
                    </button>
                    <button
                        onClick={handleExitDemo}
                        className="w-full flex items-center gap-3 px-3 py-2 rounded-lg
                            text-slate-400 hover:bg-slate-800 hover:text-white
                            transition-colors duration-150"
                        title="Return to the demo persona picker"
                    >
                        <span className="w-5 h-5 flex items-center justify-center text-base">←</span>
                        <span className="text-sm font-medium">Exit to Login</span>
                    </button>
                </div>
            </aside>
        </>
    );
}

// ============================================================================
// HEADER COMPONENT
// ============================================================================

function Header({ title, subtitle, onMenuClick, children }) {
    return (
        <header className="h-16 bg-white border-b border-slate-200 flex items-center justify-between px-4 lg:px-6">
            <div className="flex items-center gap-4">
                <button
                    onClick={onMenuClick}
                    className="lg:hidden p-2 -ml-2 text-slate-500 hover:text-slate-700"
                >
                    <NavIcon name="menu" className="w-5 h-5" />
                </button>
                <div>
                    <div className="flex items-center gap-2">
                        <h1 className="text-lg font-semibold text-slate-900 leading-tight">{title}</h1>
                        <DemoPill />
                    </div>
                    {subtitle && (
                        <p className="text-xs text-slate-500 leading-tight mt-0.5">{subtitle}</p>
                    )}
                </div>
            </div>
            <div className="flex items-center gap-3">
                {children}
            </div>
        </header>
    );
}

// ============================================================================
// MAIN LAYOUT COMPONENT
// ============================================================================

function Layout({ type, title, subtitle = null, headerActions = null, children }) {
    const [sidebarOpen, setSidebarOpen] = useState(false);
    const [profile, setProfile] = useState(null);
    const [isSuperuser, setIsSuperuser] = useState(false);
    const [loading, setLoading] = useState(true);
    const navigate = useNavigate();

    useEffect(() => {
        // Demo mode - just check localStorage
        const auth = localStorage.getItem('agenticed_admin_auth');
        if (!auth) {
            navigate('/login');
            return;
        }

        try {
            const session = JSON.parse(auth);
            setProfile({
                name: session.identifier || session.name || 'Demo User',
                email: session.email || session.identifier || 'demo@agenticed.com'
            });
            setIsSuperuser(session.isSuperuser === true);
        } catch (e) {
            console.error('Invalid auth session');
        }

        setLoading(false);
    }, [type, navigate]);

    // Session isolation heartbeat — tells the workspace SPA the admin portal is active.
    // The workspace shows a banner when both are open simultaneously so the user
    // can make an informed choice about which session to keep active.
    useEffect(() => {
        const ADMIN_KEY   = 'agenticed_admin_active';
        const INTERVAL_MS = 20_000;
        const CHANNEL     = 'agenticed_session';

        const beat = () => localStorage.setItem(ADMIN_KEY, String(Date.now()));
        beat();
        const timer = setInterval(beat, INTERVAL_MS);

        let channel = null;
        try {
            channel = new BroadcastChannel(CHANNEL);
            channel.postMessage({ source: 'admin' });
            channel.onmessage = (e) => {
                // Workspace asked us to pause — clear our heartbeat
                if (e.data?.source === 'workspace' && e.data?.action === 'pause_admin') {
                    localStorage.removeItem(ADMIN_KEY);
                    clearInterval(timer);
                }
            };
        } catch { /* BroadcastChannel not supported */ }

        return () => {
            clearInterval(timer);
            localStorage.removeItem(ADMIN_KEY);
            channel?.close();
        };
    }, []);

    if (loading) {
        return (
            <div className="min-h-screen bg-slate-100 flex items-center justify-center">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-slate-100 flex flex-col">
            <div className="flex-1 flex">
                <Sidebar
                    type={type}
                    isOpen={sidebarOpen}
                    onClose={() => setSidebarOpen(false)}
                    profile={profile}
                    isSuperuser={isSuperuser}
                />

                <div className="flex-1 flex flex-col min-w-0">
                    <Header
                        title={title}
                        subtitle={subtitle}
                        onMenuClick={() => setSidebarOpen(true)}
                    >
                        {headerActions}
                    </Header>

                    <main className="flex-1 p-4 lg:p-6 overflow-auto">
                        {children}
                    </main>
                </div>
            </div>
        </div>
    );
}

export { Layout, Header, Sidebar, NavIcon };
export default Layout;
